'use client';

import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ElementType, ReactNode } from 'react';
import { isValidElement } from 'react';

import { cn } from './lib/utils';
import { Spinner } from './spinner';
import type { LucideIcon } from 'lucide-react';
import { useWebHaptics } from 'web-haptics/react';

const buttonVariants = cva(
  `[&_svg]:-mx-0.5 relative inline-flex shrink-0 cursor-pointer items-center
  justify-center gap-2 whitespace-nowrap rounded-lg border font-medium outline-none transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 text-sm [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0`,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'px-[calc(--spacing(3)-1px)] h-8',
        icon: 'size-8',
        'icon-lg': 'size-9',
        'icon-sm': 'size-7',
        'icon-xl': "size-10 [&_svg:not([class*='size-'])]:size-5",
        'icon-xs':
          "rounded-md before:rounded-[calc(var(--radius-md)-1px)] size-6 not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
        lg: 'px-[calc(--spacing(3.5)-1px)] h-9',
        sm: 'gap-1.5 px-[calc(--spacing(2.5)-1px)] h-7',
        xl: "px-[calc(--spacing(4)-1px)] h-10 text-base [&_svg:not([class*='size-'])]:size-4.5",
        xs: "gap-1 rounded-md px-[calc(--spacing(2)-1px)] before:rounded-[calc(var(--radius-md)-1px)] h-6 text-xs [&_svg:not([class*='size-'])]:size-3.5",
      },
      variant: {
        default:
          'not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-primary bg-primary text-primary-foreground shadow-primary/24 shadow-xs [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none [:hover,[data-pressed]]:bg-primary/90',
        accent:
          'border-violet-700 bg-violet-700 text-white shadow-violet-700/24 shadow-xs [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none [:hover,[data-pressed]]:bg-violet-700/90',
        destructive:
          'not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-destructive bg-destructive text-white shadow-destructive/24 shadow-xs [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none [:hover,[data-pressed]]:bg-destructive/90',
        'destructive-outline':
          'border-destructive bg-transparent not-dark:bg-clip-padding text-destructive-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/6%)] [:disabled,:active,[data-pressed]]:shadow-none [:hover,[data-pressed]]:border-destructive/32 [:hover,[data-pressed]]:bg-destructive/4',
        ghost: 'border-transparent text-foreground sm:[:hover]:bg-accent',
        link: 'border-transparent underline-offset-4 [:hover,[data-pressed]]:underline',
        outline:
          'border-input bg-background not-dark:bg-clip-padding text-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/6%)] [:disabled,:active,[data-pressed]]:shadow-none [:hover,[data-pressed]]:bg-accent/50',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [:active,[data-pressed]]:bg-secondary/80 [:hover,[data-pressed]]:bg-secondary/90',
      },
    },
  },
);

interface ButtonProps extends useRender.ComponentProps<'button'> {
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: VariantProps<typeof buttonVariants>['size'];
  isLoading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon | (() => ReactNode) | ReactNode;
}

// Event type is derived from the public onClick prop to support custom render targets.
type ButtonClickEvent = Parameters<NonNullable<ButtonProps['onClick']>>[0];

function Button({
  className,
  variant,
  size,
  render,
  isLoading,
  children,
  leftIcon,
  rightIcon,
  onClick,
  ...props
}: ButtonProps) {
  const typeValue: ComponentProps<'button'>['type'] = render
    ? undefined
    : 'button';
  const LeftIcon = leftIcon;
  const { trigger } = useWebHaptics();

  const handleClick = (e: ButtonClickEvent) => {
    // Haptic feedback is best-effort; Safari can reject with non-Error values.
    void (trigger() ?? Promise.resolve()).catch(() => {});
    onClick?.(e);
  };
  // Render right icon consistently for both icon components and render callbacks.
  const renderRightIcon = () => {
    if (!rightIcon || isLoading) {
      return null;
    }

    if (isValidElement(rightIcon)) {
      return rightIcon;
    }

    if (
      typeof rightIcon === 'function' ||
      (typeof rightIcon === 'object' && '$$typeof' in rightIcon)
    ) {
      const RightIconComponent = rightIcon as ElementType;

      return <RightIconComponent />;
    }

    return rightIcon;
  };
  const isIconButton = size?.startsWith('icon') || false;
  const defaultProps = {
    className: cn(buttonVariants({ className, size, variant })),
    'data-slot': 'button',
    type: typeValue,
    disabled: isLoading,
    onClick: handleClick,
    children: (
      <>
        {isLoading && (
          <Spinner
            className={cn(
              'size-4 animate-spin text-white',
              variant === 'outline' && 'text-foreground',
              variant === 'ghost' && 'text-inherit',
            )}
          />
        )}
        {LeftIcon && !isLoading && <LeftIcon />}
        {isLoading && isIconButton ? null : children}
        {renderRightIcon()}
      </>
    ),
  };

  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(defaultProps, props),
    render,
  });
}

export { Button, buttonVariants };
